import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Warning,
  List, ArrowClockwise, QrCode, Camera, ArrowLeft
} from "@phosphor-icons/react";

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-BD", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

async function getBackCameraId() {
  try {
    const devices = await Html5Qrcode.getCameras();
    if (devices && devices.length > 0) {
      const back = devices.find(d =>
        d.label.toLowerCase().includes("back") ||
        d.label.toLowerCase().includes("rear") ||
        d.label.toLowerCase().includes("environment") ||
        d.label.toLowerCase().includes("0")
      );
      if (back) return back.id;
      const front = devices.find(d =>
        d.label.toLowerCase().includes("front") ||
        d.label.toLowerCase().includes("user") ||
        d.label.toLowerCase().includes("1")
      );
      if (front && devices.length === 1) return front.id;
      return devices[0].id;
    }
  } catch (e) {
    console.warn("Could not enumerate cameras:", e);
  }
  return null;
}

export default function OrganizerScanPage() {
  const scannerRef = useRef(null);
  const [mode, setMode] = useState("camera");
  const [manual, setManual] = useState("");
  const [ticket, setTicket] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [history, setHistory] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [showCameraSelect, setShowCameraSelect] = useState(false);

  const startScannerWithCameraId = (cameraId) => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }

    const html5QrCode = new Html5Qrcode("qr-reader", {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false,
    });
    scannerRef.current = html5QrCode;

    const config = {
      fps: 10,
      qrbox: { width: 220, height: 220 },
      aspectRatio: window.innerWidth < 500 ? window.innerWidth / (window.innerHeight * 0.55) : 1.5,
      disableFlip: false,
    };

    const cameraConfig = cameraId
      ? cameraId
      : { facingMode: "environment" };

    html5QrCode.start(
      cameraConfig,
      config,
      (decodedText) => {
        handlePayload(decodedText);
        html5QrCode.pause(true);
      },
      () => {}
    ).then(() => {
      setIsScanning(true);
      setCameraError(null);
    }).catch(async (err) => {
      console.error("Camera start error:", err);
      if (cameraId && cameraId !== "environment") {
        startScannerWithCameraId({ facingMode: "environment" });
        return;
      }
      setCameraError(err?.message || "Camera failed to start. Please check permissions.");
      setIsScanning(false);
    });
  };

  const startScanner = () => {
    setCameraError(null);
    setIsScanning(false);
    getBackCameraId().then((cameraId) => {
      if (cameraId) {
        startScannerWithCameraId(cameraId);
      } else {
        startScannerWithCameraId({ facingMode: "environment" });
      }
    });
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
      setIsScanning(false);
    }
  };

  useEffect(() => {
    if (mode === "camera") {
      startScanner();
    } else {
      stopScanner();
    }
    return () => stopScanner();
  
  }, [mode]);

  const handlePayload = (payload) => {
    if (!payload) return;
    setTicket(null);
    setLookupResult(null);
    setChecking(true);
    api.post("/tickets/lookup", { qr_payload: payload })
      .then(({ data }) => {
        setLookupResult(data);
        setTicket(data.ticket || null);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Lookup failed");
        setLookupResult({ valid: false, reason: "Lookup failed" });
      })
      .finally(() => setChecking(false));
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    let payload = manual.trim();
    if (!payload.startsWith("TUP-") && !payload.includes("|") && !payload.includes("/verify/")) {
      payload = "TUP-" + payload.replace(/^TUP-/i, "");
    }
    handlePayload(payload);
    setManual("");
  };

  const handleCheckIn = (qr_payload) => {
    if (!window.confirm(`Check in ticket ${ticket?.serial_number}?

This action cannot be undone.`)) return;
    setCheckingIn(true);
    api.post("/tickets/verify", { qr_payload })
      .then(({ data }) => {
        if (data.valid) {
          toast.success("Checked in!");
          setTicket(data.ticket);
          setLookupResult({ ...lookupResult, valid: false, used: true, used_by_name: data.ticket.used_by_name });
          setHistory((h) => [{
            serial: data.ticket.serial_number,
            valid: true,
            time: new Date().toISOString(),
            event_title: data.ticket.event_title,
            ticket_type_name: data.ticket.ticket_type_name,
            used_by_name: data.ticket.used_by_name,
          }, ...h].slice(0, 20));
        } else {
          toast.error(data.reason || "Check-in failed");
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Check-in failed"))
      .finally(() => setCheckingIn(false));
  };

  const resumeScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.resume();
    }
    setTicket(null);
    setLookupResult(null);
  };

  const switchCamera = (cameraId) => {
    setShowCameraSelect(false);
    startScannerWithCameraId(cameraId);
  };

  const r = lookupResult;
  const t = ticket;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-8 dark:bg-[#0B0F1A]" data-testid="organizer-scan-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="label-eyebrow">Organizer</span>
          <h1 className="font-display text-3xl sm:text-5xl mt-1 tracking-tighter dark:text-white">Scan & Check In</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setMode("camera")}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${mode === "camera" ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white" : "border-neutral-300 dark:border-neutral-700 dark:text-neutral-300 hover:border-black dark:hover:border-neutral-500"}`}
          >
            <Camera size={16} /> Camera
          </button>
          <button
            onClick={() => setMode("manual")}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${mode === "manual" ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white" : "border-neutral-300 dark:border-neutral-700 dark:text-neutral-300 hover:border-black dark:hover:border-neutral-500"}`}
          >
            <QrCode size={16} /> Manual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {mode === "camera" ? (
            <div className="relative">
              <div
                id="qr-reader"
                className="w-full bg-neutral-900 rounded-2xl overflow-hidden relative"
                style={{ minHeight: "320px" }}
              />

              {cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-900 rounded-2xl z-10 text-white text-center p-6 m-0">
                  <XCircle size={48} className="text-red-400 mb-3" />
                  <p className="font-semibold text-red-400">Camera Error</p>
                  <p className="text-sm text-neutral-400 mt-1 max-w-xs">{cameraError}</p>
                  <button
                    onClick={() => startScanner()}
                    className="mt-4 bg-white text-black px-4 py-2 text-sm font-bold rounded-lg hover:bg-neutral-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {isScanning && !cameraError && (
                <>
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
                    <div className="relative w-56 h-56">
                      <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-[#4F46E5] rounded-tl-2xl" />
                      <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-[#4F46E5] rounded-tr-2xl" />
                      <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-[#4F46E5] rounded-bl-2xl" />
                      <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-[#4F46E5] rounded-br-2xl" />
                      <div className="absolute inset-0 border-2 border-white/25 rounded-2xl" />
                    </div>
                  </div>

                  <div className="absolute top-3 left-3 z-10 flex gap-2">
                    <button
                      onClick={() => setShowCameraSelect(!showCameraSelect)}
                      className="bg-black/60 hover:bg-black/80 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                    >
                      <Camera size={14} /> Switch Camera
                    </button>
                  </div>

                  {showCameraSelect && (
                    <div className="absolute top-14 left-3 z-20 bg-black/90 rounded-lg p-2 min-w-[200px]">
                      <p className="text-white text-xs font-bold px-2 py-1 mb-1">Select Camera</p>
                      <button
                        onClick={() => switchCamera({ facingMode: "environment" })}
                        className="w-full text-left text-white text-xs px-3 py-2 rounded hover:bg-white/20 flex items-center gap-2"
                      >
                        <Camera size={12} /> Back Camera
                      </button>
                      <button
                        onClick={() => switchCamera({ facingMode: "user" })}
                        className="w-full text-left text-white text-xs px-3 py-2 rounded hover:bg-white/20 flex items-center gap-2"
                      >
                        <Camera size={12} /> Front Camera
                      </button>
                    </div>
                  )}

                  <div className="absolute bottom-3 right-3 z-10 flex gap-2">
                    <button
                      onClick={() => {
                        if (scannerRef.current) {
                          try {
                            scannerRef.current.turnOnTorchIfSupported();
                            setTorchOn(true);
                          } catch (e) {
                            toast.error("Flash not supported on this device");
                          }
                        }
                      }}
                      className="bg-black/60 hover:bg-black/80 text-white p-2 rounded-full"
                      title="Turn on flash"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
                      </svg>
                    </button>
                    <button
                      onClick={stopScanner}
                      className="bg-black/60 hover:bg-black/80 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                    >
                      Stop
                    </button>
                  </div>
                </>
              )}

              <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 mt-2">Point your camera at a ticket QR code</p>
            </div>
          ) : (
            <form onSubmit={handleManualSubmit} className="border border-neutral-200 dark:border-neutral-700 dark:bg-[#151c2c] p-6 bg-white rounded-2xl">
              <label className="label-eyebrow dark:text-neutral-400">Enter serial number</label>
              <div className="flex gap-3 mt-2">
                <input
                  value={manual}
                  onChange={(e) => setManual(e.target.value.toUpperCase())}
                  placeholder="TUP-A3K9F00001"
                  className="flex-1 border border-neutral-300 dark:border-neutral-700 dark:bg-[#0B0F1A] dark:text-white px-4 py-3 text-lg font-mono tracking-wider outline-none focus:border-[#4F46E5]"
                  data-testid="manual-serial-input"
                />
                <button type="submit" className="btn-red px-6 text-base" disabled={!manual.trim()}>
                  Look Up
                </button>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">Enter the full serial number (e.g. TUP-A3K9F00001)</p>
            </form>
          )}

          {checking && (
            <div className="border border-neutral-200 dark:border-neutral-700 dark:bg-[#151c2c] p-8 text-center bg-white rounded-2xl">
              <p className="text-neutral-500 dark:text-neutral-400 animate-pulse">Looking up ticket…</p>
            </div>
          )}

          {r && !checking && (
            <div className="bg-white dark:bg-[#151c2c] border border-neutral-200 dark:border-neutral-700 rounded-2xl overflow-hidden">
              <div
                className={`p-6 text-white text-center ${r.valid ? "bg-[#10B981]" : r.used ? "bg-[#F59E0B]" : "bg-[#0A0A0A] dark:bg-[#0B0F1A]"}`}
                data-testid="scan-result"
              >
                {r.valid ? (
                  <>
                    <CheckCircle size={48} weight="fill" className="mx-auto" />
                    <h2 className="font-display text-3xl mt-3 tracking-tighter">Valid Ticket</h2>
                    <p className="text-sm opacity-80 mt-1">Ready to check in.</p>
                  </>
                ) : r.used ? (
                  <>
                    <Warning size={48} weight="fill" className="mx-auto" />
                    <h2 className="font-display text-3xl mt-3 tracking-tighter">Already Checked In</h2>
                    <p className="text-sm opacity-80 mt-1">By <strong>{r.used_by_name}</strong></p>
                  </>
                ) : (
                  <>
                    <XCircle size={48} weight="fill" className="mx-auto" />
                    <h2 className="font-display text-3xl mt-3 tracking-tighter">Invalid Ticket</h2>
                    <p className="text-sm opacity-80 mt-1">{r.reason}</p>
                  </>
                )}
              </div>

              {t && (
                <div className="p-6 dark:bg-[#151c2c]">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Serial</p>
                      <p className="font-mono font-bold text-[#4F46E5]">{t.serial_number}</p>
                    </div>
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Event</p>
                      <p className="font-semibold dark:text-white">{t.event_title}</p>
                    </div>
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Ticket type</p>
                      <p className="dark:text-neutral-300">{t.ticket_type_name}</p>
                    </div>
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Gate</p>
                      <p className="dark:text-neutral-300">{t.gate}</p>
                    </div>
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Venue</p>
                      <p className="dark:text-neutral-300">{t.venue}</p>
                    </div>
                    <div>
                      <p className="label-eyebrow dark:text-neutral-400">Date</p>
                      <p className="dark:text-neutral-300">{t.event_date && new Date(t.event_date).toLocaleString("en-BD")}</p>
                    </div>
                    {t.user_name && (
                      <div>
                        <p className="label-eyebrow dark:text-neutral-400">Holder</p>
                        <p className="dark:text-neutral-300">{t.user_name}</p>
                      </div>
                    )}
                    {r.used && t.used_at && (
                      <div>
                        <p className="label-eyebrow dark:text-neutral-400">Checked in at</p>
                        <p className="dark:text-neutral-300">{formatTime(t.used_at)}</p>
                      </div>
                    )}
                  </div>

                  {r.valid && (
                    <button
                      onClick={() => handleCheckIn(t.qr_payload || t.serial_number)}
                      disabled={checkingIn}
                      className="mt-5 w-full btn-red text-base py-3.5 text-lg font-bold disabled:opacity-50"
                      data-testid="checkin-btn"
                    >
                      {checkingIn ? "Checking in…" : "Check In!"}
                    </button>
                  )}

                  {isScanning && (
                    <button
                      onClick={resumeScanning}
                      className="mt-3 w-full text-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white"
                    >
                      Scan another ticket
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border border-neutral-200 dark:border-neutral-700 dark:bg-[#151c2c] bg-white rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <List size={18} className="dark:text-neutral-300" />
              <span className="font-bold text-sm uppercase tracking-widest dark:text-neutral-300">Recent Scans</span>
            </div>
            {history.length > 0 && (
              <button onClick={() => setHistory([])} className="text-xs text-neutral-400 hover:text-black dark:hover:text-white flex items-center gap-1">
                <ArrowClockwise size={14} /> Clear
              </button>
            )}
          </div>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700 max-h-[500px] overflow-y-auto" data-testid="scan-history">
            {history.length === 0 ? (
              <p className="p-6 text-sm text-neutral-400 dark:text-neutral-500 text-center">No scans yet</p>
            ) : (
              history.map((h, i) => (
                <div key={i} className="p-4 flex items-start gap-3" data-testid={`history-${i}`}>
                  <div className={`mt-0.5 flex-shrink-0 ${h.valid ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                    {h.valid ? <CheckCircle size={20} weight="fill" /> : <Warning size={20} weight="fill" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs font-bold text-[#4F46E5]">{h.serial}</p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-300 truncate">{h.event_title}</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                      {formatTime(h.time)}
                      {h.used_by_name && ` · ${h.used_by_name}`}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        #qr-reader video {
          width: 100% !important;
          height: auto !important;
          min-height: 320px !important;
          object-fit: fill !important;
          display: block !important;
        }
        #qr-reader {
          max-width: 100% !important;
        }
        #qr-reader__scan_region {
          background: transparent !important;
          min-height: 320px !important;
        }
        #qr-reader__dashboard {
          display: none !important;
        }
        #qr-reader__header_message {
          display: none !important;
        }
        #qr-reader__status_span {
          display: none !important;
        }
        #qr-reader img {
          display: none !important;
        }
        .qr-shaded-region {
          background: transparent !important;
        }
        #qr-reader__dashboard_section_swaplink {
          display: none !important;
        }
      `}</style>
    </div>
  );
}